/* © 2023 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Stack } from '@mui/material'
import { StatusIcon, Button, Link, Message, useDocumentTitle } from '@prom/web-embers'
import { FeedbackMessage, ThirdPartyButtons } from '../components'
import { useForm } from 'react-hook-form'
import { emailValidation, parseUrl } from '../utils/formValidation'
import { makeForm } from '../utils/formUtils'
import { OIDC_CONFIG_UNION } from '../constants'
import { LOGIN_ERROR } from '../constants/errors'
import { saveToLocalStorage, recoverFromLocalStorage, clearFormData, RECOVER_PATH } from '../utils/localStorageUtils'
import tracker from '../services/mixpanelService'
import { isFromApp } from '../utils/urlUtils'
import { isIOS, isIpadOS } from '../utils/platformUtils'
import { StyledEmailLoginPage } from './styles'
import classNames from 'classnames'
import { PromFormInput as Input } from '@prom/business-components'

interface IEmailLogin {
  error: string
}

const SAVED_FIELD_KEYS = ['email']

const EmailLogin = (props: IEmailLogin): React.ReactElement => {
  const { error } = props
  const [globalError, setGlobalError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const intl = useIntl()
  const { register, handleSubmit, watch, formState, setError, setValue, clearErrors, control } = useForm({
    mode: 'onBlur',
  })
  const { errors } = formState
  const { email, password } = errors

  const [canSubmit, setCanSubmit] = useState<boolean>(false)

  const requiredFields = watch(['email', 'password'])

  useDocumentTitle(intl.formatMessage({ id: 'pageTitle.signInWithEmail' }))

  useEffect(() => {
    if (!(requiredFields.includes(undefined) || requiredFields.includes(''))) setCanSubmit(true)
    else setCanSubmit(false)
  }, [requiredFields])

  useEffect(() => {
    OIDC_CONFIG_UNION.EMAIL && setValue('email', OIDC_CONFIG_UNION.EMAIL)
  }, [OIDC_CONFIG_UNION.EMAIL])

  useEffect(() => {
    if (!error) {
      setGlobalError('')
      clearFormData()
      return
    }

    if (error.endsWith(LOGIN_ERROR.PASSWORD_TOO_MANY_REQUESTS)) {
      setError('password', {
        message: intl.formatMessage({
          id: 'error.limitExceeded',
        }),
      })
    } else {
      setGlobalError(
        intl.formatMessage({
          id: 'loginFailureErrMsg',
        }),
      )
    }
    recoverFromLocalStorage(RECOVER_PATH.login, setValue)
  }, [error])

  const onLogin = (data: Record<string, string>) => {
    setLoginLoading(true)
    saveToLocalStorage(RECOVER_PATH.login, SAVED_FIELD_KEYS, data)
    tracker.signInSubmit()
    makeForm(data, 'post', 'login')

    // the hack way to fix the issue that click the cancel to interrupt 302 redirect when awake app from the ios browser
    if (isFromApp(window.location.search) && (isIpadOS() || isIOS())) {
      setTimeout(() => {
        setLoginLoading(false)
      }, 3000)
    }
  }

  return (
    <StyledEmailLoginPage
      className="EmailLogin-root"
      cardProps={{
        title: intl.formatMessage({ id: 'login.email', defaultMessage: 'Sign in with email' }),
        feedbackMessage: (
          <>
            <FeedbackMessage
              redirectFrom={[RECOVER_PATH.verifyAccount, RECOVER_PATH.resetPassword]}
              gap="0 0 16px"
              messages={{
                [RECOVER_PATH.verifyAccount]: intl.formatMessage({
                  id: 'verifyAccount.verified.message.content',
                }),
                [RECOVER_PATH.resetPassword]: intl.formatMessage({
                  id: 'resetPassword.reset.message.content',
                }),
              }}
            />
            {globalError && (
              <Stack marginBottom={2}>
                <Message type="error" align="left">
                  {globalError}
                </Message>
              </Stack>
            )}
          </>
        ),
        children: (
          <>
            <form name="emailLogin" onSubmit={handleSubmit(onLogin)}>
              <Input
                name="email"
                control={control}
                inputProps={{
                  id: 'email',
                  className: 'Layout-formItem',
                  fullWidth: true,
                  disabled: loginLoading,
                  inputProps: {
                    maxLength: 320,
                  },
                  label: intl.formatMessage({ id: 'email', defaultMessage: 'Email' }),
                  extraMessage: !!email?.message && (
                    <StatusIcon type="error" variant="filled" size="small">
                      {errors.email?.message}
                    </StatusIcon>
                  ),
                  color: email?.message ? 'error' : 'primary',
                }}
                onChange={() => clearErrors('email')}
                rules={{
                  validate: value => emailValidation(value, 'email', intl, setValue),
                }}
              />
              <Input
                name="password"
                control={control}
                inputProps={{
                  id: 'password',
                  className: 'Layout-formItem last',
                  fullWidth: true,
                  disabled: loginLoading,
                  isPassword: true,
                  label: intl.formatMessage({ id: 'password', defaultMessage: 'Password' }),
                  autoComplete: 'new-password',
                  extraMessage: !!password?.message && (
                    <StatusIcon type="error" variant="filled" size="small">
                      {errors.password?.message}
                    </StatusIcon>
                  ),
                  color: password?.message ? 'error' : 'primary',
                }}
                onChange={() => clearErrors('password')}
              />
              <input
                type="hidden"
                id="oauth_request"
                {...register('oauth_request')}
                value={parseUrl(OIDC_CONFIG_UNION.OAUTH_REQUEST)}
              />
              <Stack marginTop={1.25} marginLeft={2} marginBottom={4} textAlign="left">
                <Link
                  role="link"
                  size="small"
                  disabled={loginLoading}
                  onClick={() => {
                    tracker.forgotPasswordStart()
                    window.location.assign(parseUrl(OIDC_CONFIG_UNION.FORGOT_PASSWORD_LINK))
                  }}
                >
                  {intl.formatMessage({ id: 'forgotPassword', defaultMessage: 'Forgot password?' })}
                </Link>
              </Stack>
              <Button
                data-testid="submitButton"
                fullWidth
                type="submit"
                size="large"
                disabled={!(Object.keys(errors).length === 0 && canSubmit)}
                loading={loginLoading}
              >
                {loginLoading
                  ? intl.formatMessage({
                      id: 'signingIn',
                    })
                  : intl.formatMessage({ id: 'signInNormal' })}
              </Button>
            </form>
            {OIDC_CONFIG_UNION.LOGIN !== 'imported' && (
              <>
                <div className="EmailLogin-dividerText">
                  <i />
                  <span>
                    {intl.formatMessage({
                      id: 'login.divider',
                    })}
                  </span>
                </div>
                <ThirdPartyButtons
                  className="EmailLogin-thirdPartyButtons"
                  mode="horizontal"
                  type="login"
                  providers={OIDC_CONFIG_UNION.PROVIDERS || []}
                  disabled={loginLoading}
                />
              </>
            )}
            <Stack display="flex" alignItems="center">
              <Link
                role="link"
                disabled={loginLoading}
                onClick={() => window.location.assign(parseUrl(OIDC_CONFIG_UNION.REGISTER_LINK))}
                className={classNames('EmailLogin-createAccountLink', {
                  'EmailLogin-createAccountLink-imported': OIDC_CONFIG_UNION.LOGIN === 'imported',
                })}
              >
                {intl.formatMessage({ id: 'login.needAccount' })}
              </Link>
            </Stack>
          </>
        ),
      }}
    />
  )
}

export default EmailLogin
